import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSystemLog, type SystemLogFilters } from "../api/systemLog";
import { SystemLogFilterBar } from "../components/SystemLogFilterBar";
import { SystemLogBlock } from "../components/SystemLogBlock";

function filtersFromParams(params: URLSearchParams): SystemLogFilters {
  return {
    subsystem: params.get("subsystem") ?? undefined,
    since: params.get("since") ?? undefined,
    until: params.get("until") ?? undefined,
  };
}

function paramsFromFilters(filters: SystemLogFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.subsystem) p.set("subsystem", filters.subsystem);
  if (filters.since) p.set("since", filters.since);
  if (filters.until) p.set("until", filters.until);
  return p;
}

export function SystemLogViewer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    useSystemLog(filters);

  function handleApply(next: SystemLogFilters) {
    setSearchParams(paramsFromFilters(next));
  }

  const allItems = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <SystemLogFilterBar initial={filters} onApply={handleApply} />

      {isLoading && <div className="status-line">Loading…</div>}
      {error && (
        <div className="status-line error">
          Error: {error instanceof Error ? error.message : String(error)}
        </div>
      )}
      {!isLoading && !error && allItems.length === 0 && (
        <div className="status-line">No errors match those filters.</div>
      )}

      <div className="message-list">
        {allItems.map((item) => (
          <SystemLogBlock key={item.id} item={item} />
        ))}
      </div>

      {hasNextPage && (
        <div className="load-more">
          <button
            type="button"
            className="btn"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Load older"}
          </button>
        </div>
      )}
    </div>
  );
}
